import makeAxiosRequest, { GetAxiosResponse } from './axios.instance';

export interface RequestList {
  auth: {
    signIn: GetAxiosResponse;
    signUp: GetAxiosResponse;
    getProfile: GetAxiosResponse;
    getPermissions: GetAxiosResponse;
    updateProfile: GetAxiosResponse;
    confirmEmail: GetAxiosResponse;
    resetPassword: GetAxiosResponse;
    changePassword: GetAxiosResponse;
    confirmResetPassword: GetAxiosResponse;
  };
  spotify: {
    getSpotifyArtist: GetAxiosResponse;
    getArtistById: GetAxiosResponse;
    getTopTracks: GetAxiosResponse;
    getAllTracks: GetAxiosResponse;
    searchTracks: GetAxiosResponse;
  };
  deals: {
    getDealList: GetAxiosResponse;
    createDeal: GetAxiosResponse;
    dealSubmit: GetAxiosResponse;
    getDeal: GetAxiosResponse;
    updateDeal: GetAxiosResponse;
    calculateAdvance: GetAxiosResponse;
    uploadReport: GetAxiosResponse;
    deleteDeal: GetAxiosResponse;
  };
  reports: {
    createInitialReport: GetAxiosResponse;
    finaliseReport: GetAxiosResponse;
    createInitialReportLocalStorage: GetAxiosResponse;
    remove: GetAxiosResponse;
    validateLocalReceipts: GetAxiosResponse;
    submitLocalReceipts: GetAxiosResponse;
    removeFromLocalStorage: GetAxiosResponse;
    statusUploaded: GetAxiosResponse;
    getDownloadUrl: GetAxiosResponse;
  };
  analytics: {
    getEngagement: GetAxiosResponse;
    getStreamsByPlatform: GetAxiosResponse;
    getStreamsByTrack: GetAxiosResponse;
    searchTracks: GetAxiosResponse;
    getTopCountries: GetAxiosResponse;
    getAllPlatforms: GetAxiosResponse;
    getArtistProfiles: GetAxiosResponse;
    getAdminAnalytics: GetAxiosResponse;
    socialPlatformEngagements: GetAxiosResponse;
    socialPlatformAudiences: GetAxiosResponse;
    getCompletionRateArtists: GetAxiosResponse;
    getCompletionRateTracks: GetAxiosResponse;
    getCompletionRateAnalytics: GetAxiosResponse;
    getCompletionRateRankList: GetAxiosResponse;
    getCompletionRateRank: GetAxiosResponse;
  };
  invitation: {
    validate: GetAxiosResponse;
    createInvitation: GetAxiosResponse;
    getInvitedArtists: GetAxiosResponse;
    updateInvitation: GetAxiosResponse;
  };
  edu: {
    getPublic: GetAxiosResponse;
  };
  recoupments: {
    renewals: GetAxiosResponse;
    statusUpdate: GetAxiosResponse;
  };
  preference: {
    account: GetAxiosResponse;
    updateWeeklyAnalyticsEmail: GetAxiosResponse;
  };
  artistProfile: {
    list: GetAxiosResponse;
    update: GetAxiosResponse;
    uploadBankingProof: GetAxiosResponse;
    complete: GetAxiosResponse;
  };
  referral: {
    createReferral: GetAxiosResponse;
  };
  collabClub: {
    getArtists: GetAxiosResponse;
    searchArtists: GetAxiosResponse;
    getLikedArtists: GetAxiosResponse;
    likeArtist: GetAxiosResponse;
    unlikeArtist: GetAxiosResponse;
    submitCollabRequest: GetAxiosResponse;
    getMyCcArtists: GetAxiosResponse;
    getProfileRequests: GetAxiosResponse;
    getProfileCurrentMonthRequests: GetAxiosResponse;
    getProfileStats: GetAxiosResponse;
    updateMyProfile: GetAxiosResponse;
    getAdminRequests: GetAxiosResponse;
    createAdminRequest: GetAxiosResponse;
    previewAdminRequest: GetAxiosResponse;
    updateAdminArtist: GetAxiosResponse;
    getAdminMatches: GetAxiosResponse;
    previewAdminMatch: GetAxiosResponse;
    createAdminMatch: GetAxiosResponse;
    getAdminMetrics: GetAxiosResponse;
  };
  publishing: {
    getView: GetAxiosResponse;
    createBatch: GetAxiosResponse;
    answerIdentity: GetAxiosResponse;
    mark: GetAxiosResponse;
    unmark: GetAxiosResponse;
    getAdminItems: GetAxiosResponse;
    getAdminArtists: GetAxiosResponse;
    getIngestRuns: GetAxiosResponse;
  };
}

const api = {
  auth: {
    signIn: makeAxiosRequest('/auth/login', 'post'),
    signUp: makeAxiosRequest('/users', 'post'),
    getProfile: makeAxiosRequest('auth/profile', 'get'),
    getPermissions: makeAxiosRequest('auth/permissions', 'get'),
    updateProfile: makeAxiosRequest('/users', 'patch'),
    confirmEmail: makeAxiosRequest('/users/confirm-email', 'post'),
    resetPassword: makeAxiosRequest('/users/reset-password', 'post'),
    changePassword: makeAxiosRequest('/users/change-password', 'post'),
    confirmResetPassword: makeAxiosRequest('/users/confirm-reset-password', 'post'),
  },
  spotify: {
    getSpotifyArtist: makeAxiosRequest(
      (query) => `/spotify/get-artist/${encodeURIComponent(`${query}`)}`,
      'get'
    ),
    // lightweight name+image lookup for the profile's Spotify URL preview (see wiki spec)
    getArtistById: makeAxiosRequest(
      (id) => `/spotify/artist/${encodeURIComponent(`${id}`)}`,
      'get'
    ),
    getTopTracks: makeAxiosRequest((id) => `/spotify/get-top-tracks/${id}`, 'get'),
    getAllTracks: makeAxiosRequest((id) => `/spotify/get-all-tracks/${id}`, 'get'),
    searchTracks: makeAxiosRequest('/spotify/search-tracks', 'get'),
  },
  deals: {
    getDealList: makeAxiosRequest('/deals/list', 'get'),
    createDeal: makeAxiosRequest('/deals', 'post'),
    dealSubmit: makeAxiosRequest((id) => `/deals/${id}/submit`, 'post'),
    getDeal: makeAxiosRequest((id) => `/deals/${id}`, 'get'),
    updateDeal: makeAxiosRequest((id) => `/deals/${id}`, 'put'),
    calculateAdvance: makeAxiosRequest((id) => `/deals/${id}/advance`, 'get'),
    uploadReport: makeAxiosRequest((id) => `/deals/${id}/file`, 'post'),
    deleteDeal: makeAxiosRequest((id) => `/deals/${id}`, 'delete'),
  },
  reports: {
    createInitialReport: makeAxiosRequest((id) => `/reports/deal/${id}`, 'post'),
    finaliseReport: makeAxiosRequest('/reports', 'post'),
    createInitialReportLocalStorage: makeAxiosRequest(
      (id) => `/reports/receipt-parsing/deal/${id}`,
      'post'
    ),
    remove: makeAxiosRequest((id) => `/reports/${id}`, 'delete'),
    validateLocalReceipts: makeAxiosRequest((id) => `/reports/validate-receipts/deal/${id}`, 'get'),
    submitLocalReceipts: makeAxiosRequest((id) => `/reports/submit-review/deal/${id}`, 'post'),
    removeFromLocalStorage: makeAxiosRequest((id) => `/reports/localStorage/${id}`, 'delete'),
    statusUploaded: makeAxiosRequest((id) => `/reports/${id}/status-uploaded`, 'post'),
    getDownloadUrl: makeAxiosRequest((id) => `/reports/${id}/get-download-url`, 'get'),
  },
  analytics: {
    getEngagement: makeAxiosRequest(() => `/analytics/engagements`, 'get'),
    getStreamsByPlatform: makeAxiosRequest(() => `/analytics/streams/platforms`, 'get'),
    getStreamsByTrack: makeAxiosRequest(() => `/analytics/streams/tracks`, 'get'),
    searchTracks: makeAxiosRequest(() => `/analytics/tracks`, 'get'),
    getTopCountries: makeAxiosRequest(() => `/analytics/countries`, 'get'),
    getAllPlatforms: makeAxiosRequest(() => `/analytics/platforms`, 'get'),
    getArtistProfiles: makeAxiosRequest(() => `/analytics/profiles`, 'get'),
    getAdminAnalytics: makeAxiosRequest((id) => `/analytics/admin-reports/${id}`, 'get'),
    socialPlatformEngagements: makeAxiosRequest(
      () => `/analytics/social-platforms/engagements`,
      'get'
    ),
    socialPlatformAudiences: makeAxiosRequest(() => `/analytics/social-platforms/audiences`, 'get'),
    getCompletionRateArtists: makeAxiosRequest(() => `/analytics/completion/artists`, 'get'),
    getCompletionRateTracks: makeAxiosRequest(() => `/analytics/completion/tracks`, 'get'),
    getCompletionRateAnalytics: makeAxiosRequest(() => `/analytics/completion/analytics`, 'get'),
    getCompletionRateRankList: makeAxiosRequest(() => `/analytics/completion/rank-list`, 'get'),
    getCompletionRateRank: makeAxiosRequest(() => `/analytics/completion/rank`, 'get'),
  },
  invitation: {
    validate: makeAxiosRequest(() => `/invitation/validate`, 'post'),
    createInvitation: makeAxiosRequest(() => `/invitation`, 'post'),
    getInvitedArtists: makeAxiosRequest(() => `/invitation`, 'get'),
    updateInvitation: makeAxiosRequest((id) => `/invitation/${id}`, 'patch'),
  },
  edu: {
    getPublic: makeAxiosRequest(() => `/edu/public`, 'get'),
  },
  recoupments: {
    renewals: makeAxiosRequest('/recoupments/renewals', 'get'),
    statusUpdate: makeAxiosRequest((id) => `/recoupments/${id}/renewals/status`, 'put'),
  },
  preference: {
    account: makeAxiosRequest('/preference/account', 'get'),
    updateWeeklyAnalyticsEmail: makeAxiosRequest(
      '/preference/account/update-weekly-analytics-email',
      'put'
    ),
  },
  artistProfile: {
    list: makeAxiosRequest('/artist-profile', 'get'),
    update: makeAxiosRequest((id) => `/artist-profile/${id}`, 'patch'),
    uploadBankingProof: makeAxiosRequest((id) => `/artist-profile/${id}/banking-proof`, 'post'),
    complete: makeAxiosRequest((id) => `/artist-profile/${id}/complete`, 'post'),
  },
  referral: {
    createReferral: makeAxiosRequest(() => `/referral`, 'post'),
  },
  collabClub: {
    getArtists: makeAxiosRequest('/cc-artists', 'get'),
    searchArtists: makeAxiosRequest('/cc-artists/search', 'post'),
    getLikedArtists: makeAxiosRequest('/cc-artists/liked', 'get'),
    likeArtist: makeAxiosRequest('/cc-artists/like', 'post'),
    unlikeArtist: makeAxiosRequest((artistId) => `/cc-artists/like/${artistId}`, 'delete'),
    submitCollabRequest: makeAxiosRequest('/cc-artists/requests', 'post'),
    getMyCcArtists: makeAxiosRequest('/cc-artists/my-profiles', 'get'),
    getProfileRequests: makeAxiosRequest(
      (artistId) => `/cc-artists/my-profiles/${artistId}/requests`,
      'get'
    ),
    getProfileCurrentMonthRequests: makeAxiosRequest(
      (artistId) => `/cc-artists/my-profiles/${artistId}/requests/current-month`,
      'get'
    ),
    getProfileStats: makeAxiosRequest(
      (artistId) => `/cc-artists/my-profiles/${artistId}/requests/stats`,
      'get'
    ),
    updateMyProfile: makeAxiosRequest((artistId) => `/cc-artists/my-profiles/${artistId}`, 'patch'),
    getAdminRequests: makeAxiosRequest('/admin/cc-requests', 'get'),
    createAdminRequest: makeAxiosRequest('/admin/cc-requests', 'post'),
    previewAdminRequest: makeAxiosRequest('/admin/cc-requests/preview', 'post'),
    updateAdminArtist: makeAxiosRequest((id) => `/admin/cc-artists/${id}`, 'patch'),
    getAdminMatches: makeAxiosRequest('/admin/cc-matches', 'get'),
    previewAdminMatch: makeAxiosRequest('/admin/cc-matches/preview', 'post'),
    createAdminMatch: makeAxiosRequest('/admin/cc-matches', 'post'),
    getAdminMetrics: makeAxiosRequest('/admin/cc-metrics', 'get'),
  },
  publishing: {
    getView: makeAxiosRequest((id) => `/publishing/artists/${id}/view`, 'get'),
    createBatch: makeAxiosRequest((id) => `/publishing/artists/${id}/batches`, 'post'),
    answerIdentity: makeAxiosRequest((id) => `/publishing/artists/${id}/identity-answers`, 'post'),
    // `id` is the pre-built "<spotifyId>/tracks/<isrc>" segment (url fn takes one id)
    mark: makeAxiosRequest((id) => `/publishing/artists/${id}/mark`, 'put'),
    unmark: makeAxiosRequest((id) => `/publishing/artists/${id}/mark`, 'delete'),
    getAdminItems: makeAxiosRequest('/admin/publishing/items', 'get'),
    getAdminArtists: makeAxiosRequest('/admin/publishing/artists', 'get'),
    getIngestRuns: makeAxiosRequest('/publishing/ingest-runs', 'get'),
  },
} as RequestList;

export default api;
